import { CheckCircleOutlined, CloseCircleOutlined } from '@ant-design/icons';
import { notification, Tooltip } from 'antd';
import { FavouriteIconButton } from 'components/common/FavouriteIconButton';
import { useDel, usePut } from 'hooks';
import { useCallback, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  exportTemplateActions as actions,
  exportTemplateSelectors as selectors,
} from 'reducers/exportTemplate/reducer';
import { exportTemplates } from 'services/api';
import { favourites as strings } from './strings';

const SuccessIcon = (
  <CheckCircleOutlined style={{ fontSize: 20, color: 'green' }} />
);
const ErrorIcon = (
  <CloseCircleOutlined style={{ fontSize: 20, color: 'red' }} />
);

/**
 * Button for toggling a template as the user's favourite.
 * @param {Object} props
 * @param {number} props.templateId - The ID of the template.
 * @param {string} props.templateName - The name of the template.
 */
export const TemplateFavouriteButton = props => {
  const dispatch = useDispatch();

  const isActive = useSelector(state =>
    selectors.isTemplateIdFavourite(state, props.templateId)
  );

  const isFavouriteLoading = useSelector(selectors.isFavouriteLoading);

  const onRequestSuccess = useCallback(() => {
    dispatch(actions.favouriteTemplateSet(isActive ? null : props.templateId));
    notification.open({
      icon: SuccessIcon,
      message: isActive ? strings.removeSuccessTitle : strings.successTitle,
      description:
        !isActive && props.templateName
          ? strings.successDescription(props.templateName)
          : undefined,
      placement: 'bottomLeft',
    });
  }, [isActive, props.templateId, props.templateName, dispatch]);

  const onRequestError = useCallback(() => {
    dispatch(actions.favouriteSettingFailed());
    notification.open({
      icon: ErrorIcon,
      message: strings.errorTitle,
      description: strings.errorDescription,
      placement: 'bottomLeft',
    });
  }, [dispatch]);

  const requestConfig = {
    configuration: {
      onRequestSuccess,
      onRequestError,
      onRequestBegan: () => dispatch(actions.favouriteSettingRequested()),
    },
  };

  const [putState, sendPut] = usePut({
    url: `${exportTemplates}/favourite/${props.templateId}`,
    ...requestConfig,
  });

  const [delState, sendDel] = useDel({
    url: `${exportTemplates}/favourite`,
    ...requestConfig,
  });

  const requestState = isActive ? delState : putState;

  // If the component unmounts before the request finishes - notify the store
  useEffect(() => {
    return () => {
      if (putState.loading || delState.loading) {
        dispatch(actions.favouriteSettingFailed());
      }
    };
  }, [putState.loading, delState.loading, dispatch]);

  const onPress = () => {
    if (isFavouriteLoading) return;
    if (isActive) {
      sendDel();
    } else {
      sendPut();
    }
  };

  const isDisabled = isFavouriteLoading;

  return (
    <Tooltip
      title={
        requestState.loading
          ? strings.tooltipLoading
          : isActive
          ? strings.tooltipActive
          : strings.tooltipSet
      }
      placement='right'
    >
      <FavouriteIconButton
        data-testid={`template-favourite-button-${props.templateId}`}
        disabled={isDisabled}
        value={isActive}
        onClick={onPress}
        loading={requestState.loading}
      />
    </Tooltip>
  );
};
